import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Eye, EyeOff, KeyRound, LogOut, User, Edit3 } from "lucide-react";
import { Outlet, useNavigate } from "react-router-dom";
import Sidebar from "../../GlobalComponents/Sidebar";
import { AuthContext } from "../../context/AuthContext";
import axios from "../../api/axios";

// Direct exports from MasterCssClass.js
import {
  masterDashboardRoot,
  masterDashboardToggleBtnBase,
  masterDashboardHeaderWrap,
  masterDashboardTitleTag,
  masterDashboardUserEmail,
  masterDashboardProfileTrigger,
  masterDashboardAvatarBox,
  masterDashboardUserName,
  masterDashboardUserRole,
  masterDashboardDropdownPanel,
  masterDashboardDropdownItem,
  masterDashboardLogoutItem,
  masterDashboardViewArea,
  masterModalOverlay,
  masterModalBox,
  masterModalHeader,
  masterModalTitle,
  masterModalSubText,
  masterModalCloseBtn,
  masterModalLabel,
  masterModalInput,
  masterModalSubmitBtn,
  masterModalMsgSuccess,
  masterModalMsgError,
} from "../../GlobalComponents/MasterCssClass";

const Header = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  
  // Password states (untouched)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: "", text: "" });

  // Edit Profile states with field-level edit toggles and verification states
  const [profileForm, setProfileForm] = useState({ name: "", phone: "", email: "" });
  const [initialEmail, setInitialEmail] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  
  const [showEmailWarningBox, setShowEmailWarningBox] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: "", text: "" });

  const dropdownRef = useRef(null);
  const { user, logout, login } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || "",
        phone: user.phone || "",
        email: user.email || "",
      });
      setInitialEmail(user.email || "");
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setProfileOpen(false);
    logout();
    navigate("/login");
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordMessage({ type: "", text: "" });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    setIsEditingName(false);
    setIsEditingPhone(false);
    setIsEditingEmail(false);
    setShowEmailWarningBox(false);
    setOtpSent(false);
    setIsEmailVerified(false);
    setEmailOtp("");
    setProfileMessage({ type: "", text: "" });
    if (user) {
      setProfileForm({
        name: user.name || "",
        phone: user.phone || "",
        email: user.email || "",
      });
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: "error", text: "NEW_PASSWORD_AND_CONFIRM_PASSWORD_MUST_MATCH" });
      return;
    }

    setPasswordLoading(true);
    setPasswordMessage({ type: "", text: "" });
    try {
      const res = await axios.post("/auth/change-password", {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordMessage({ type: "success", text: res.data?.message || "PASSWORD_CHANGED_SUCCESSFULLY" });
      setTimeout(() => {
        closePasswordModal();
        logout();
        navigate("/login");
      }, 900);
    } catch (error) {
      setPasswordMessage({
        type: "error",
        text: error?.response?.data?.message || "PASSWORD_CHANGE_FAILED",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle email OTP sending or OTP verification
  const handleEmailAction = async () => {
    setProfileMessage({ type: "", text: "" });
    const emailChanged = profileForm.email.trim().toLowerCase() !== initialEmail.toLowerCase();

    if (!emailChanged) {
      setIsEditingEmail(false);
      setShowEmailWarningBox(false);
      return;
    }

    if (!otpSent) {
      // Step 1: Send OTP to new email
      setProfileLoading(true);
      try {
        await axios.post("/auth/send-profile-email-otp", { newEmail: profileForm.email });
        setOtpSent(true);
        setShowEmailWarningBox(false);
        setProfileMessage({ type: "success", text: "OTP sent to your new email. Please enter it to verify." });
      } catch (err) {
        setProfileMessage({ type: "error", text: err?.response?.data?.message || "Failed to send OTP." });
      } finally {
        setProfileLoading(false);
      }
    } else {
      // Step 2: Confirm OTP
      if (!emailOtp.trim()) {
        setProfileMessage({ type: "error", text: "Please enter the OTP." });
        return;
      }
      setProfileLoading(true);
      try {
        await axios.post("/auth/verify-profile-email-otp", { newEmail: profileForm.email, otp: emailOtp });
        setIsEmailVerified(true);
        setIsEditingEmail(false);
        setProfileMessage({ type: "success", text: "Email verified successfully!" });
      } catch (err) {
        setProfileMessage({ type: "error", text: err?.response?.data?.message || "Invalid or expired OTP." });
      } finally {
        setProfileLoading(false);
      }
    }
  };

  // Final Profile Save Handler (Blocks saving if email changed but not verified)
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileMessage({ type: "", text: "" });

    const emailChanged = profileForm.email.trim().toLowerCase() !== initialEmail.toLowerCase();
    if (emailChanged && !isEmailVerified) {
      setProfileMessage({ type: "error", text: "Cannot save! This email is your User ID. You must verify your new email address using OTP first." });
      return;
    }

    setProfileLoading(true);
    try {
      const payload = {
        name: profileForm.name,
        phone: profileForm.phone,
        email: profileForm.email,
      };

      const res = await axios.put("/auth/update-profile", payload);
      setProfileMessage({ type: "success", text: "Profile updated successfully!" });
      
      if (res.data?.user) {
        login({ ...res.data, token: localStorage.getItem("token") || res.data.token });
      }

      setTimeout(() => {
        closeProfileModal();
      }, 1500);
    } catch (err) {
      setProfileMessage({ type: "error", text: err?.response?.data?.message || "Profile update failed." });
    } finally {
      setProfileLoading(false);
    }
  };

  return (
    <div className={masterDashboardRoot}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
      />

      <button
        type="button"
        onClick={() => setSidebarCollapsed((prev) => !prev)}
        className={`${masterDashboardToggleBtnBase} ${sidebarCollapsed ? "left-4" : "left-[250px]"}`}
        aria-label={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
      >
        {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      <div className={`relative h-screen flex-1 bg-[#F8FAFC] ${sidebarCollapsed ? "" : "lg:ml-64"}`}>
        
        {/* HEADER SECTION */}
        <div className={masterDashboardHeaderWrap}>
          <div className="flex items-center justify-between gap-3">
            
            <div className={`${sidebarCollapsed ? "lg:pl-8" : "lg:pl-72"} transition-all duration-200 flex-1 text-center md:text-left`}>
             <p className={masterDashboardTitleTag}>
                {String(user?.role || "").toLowerCase() === "admin" || String(user?.email || "").toLowerCase() === "medhashala@gmail.com" || String(user?.email || "").toLowerCase() === "admin@gmail.com"
                  ? `Admin Panel${user?.name ? ` - ${user.name}` : ""}`
                  : String(user?.role || "").toLowerCase() === "student"
                    ? `Student Panel - ${user?.orgName || user?.institutionName || user?.organizationName || user?.tenantName || user?.name || "Organization"}`
                    : `Organization Panel - ${user?.name || user?.orgName || "Organization"}`}
              </p>
              <p className={masterDashboardUserEmail}>{user?.email || "-"}</p>
            </div>

            {/* Profile Dropdown Container */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setProfileOpen((prev) => !prev)}
                className={masterDashboardProfileTrigger}
              >
                <div className={masterDashboardAvatarBox}>
                  <User size={16} strokeWidth={2.5} />
                </div>
                <div className="hidden text-left md:block">
                  <p className={masterDashboardUserName}>{user?.name || "User"}</p>
                  <p className={masterDashboardUserRole}>{user?.role || "Student"}</p>
                </div>
                <ChevronDown size={12} className={`text-[#0E3652] transition-transform duration-200 ${profileOpen ? "rotate-180" : ""}`} />
              </button>

              {profileOpen && (
                <div className={masterDashboardDropdownPanel}>
               <button
                    onClick={() => {
                      if (user) {
                        setProfileForm({
                          name: user.name || "",
                          phone: user.phone || "",
                          email: user.email || "",
                        });
                        setInitialEmail(user.email || "");
                      }
                      setShowProfileModal(true);
                      setProfileOpen(false);
                    }}
                    className={masterDashboardDropdownItem}
                  >
                    <Edit3 size={14} /> Edit Profile
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordModal(true);
                      setProfileOpen(false);
                    }}
                    className={masterDashboardDropdownItem}
                  >
                    <KeyRound size={14} /> Change Password
                  </button>
                  <button
                    onClick={handleLogout}
                    className={masterDashboardLogoutItem}
                  >
                    <LogOut size={14} /> Logout
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* INNER SCROLL VIEW CONTAINER */}
        <div className={masterDashboardViewArea}>
          <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden">
            <Outlet />
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showProfileModal && (
        <div className={masterModalOverlay}>
          <div className={masterModalBox}>
            <div className={masterModalHeader}>
              <div>
                <h2 className={masterModalTitle}>Edit Profile</h2>
                <p className={masterModalSubText}>Click Edit on any field to modify.</p>
              </div>
              <button type="button" onClick={closeProfileModal} className={masterModalCloseBtn}>
                Cancel
              </button>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-2">
              {/* Name Field */}
              <div>
                <label className={masterModalLabel}>Full Name / Institution Name</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    disabled={!isEditingName}
                    value={profileForm.name}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, name: e.target.value.toUpperCase() }))}
                    className={`${masterModalInput} ${!isEditingName ? "bg-slate-100 text-slate-500" : ""}`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setIsEditingName((prev) => !prev)}
                    className={`px-3 py-1 text-xs font-bold rounded-xl shrink-0 cursor-pointer transition-colors ${
                      isEditingName ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#0E3652] hover:bg-[#154668] text-white"
                    }`}
                  >
                    {isEditingName ? "Lock" : "Edit"}
                  </button>
                </div>
              </div>

              {/* Mobile Number Field */}
              <div>
                <label className={masterModalLabel}>Mobile Number</label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    maxLength={10}
                    disabled={!isEditingPhone}
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, "") }))}
                    className={`${masterModalInput} ${!isEditingPhone ? "bg-slate-100 text-slate-500" : ""}`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setIsEditingPhone((prev) => !prev)}
                    className={`px-3 py-1 text-xs font-bold rounded-xl shrink-0 cursor-pointer transition-colors ${
                      isEditingPhone ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#0E3652] hover:bg-[#154668] text-white"
                    }`}
                  >
                    {isEditingPhone ? "Lock" : "Edit"}
                  </button>
                </div>
              </div>

              {/* Email Address Field with Warning & Verify Button */}
              <div>
                <label className={masterModalLabel}>Email Address (User ID)</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    disabled={!isEditingEmail || isEmailVerified}
                    value={profileForm.email}
                    onChange={(e) => {
                      setProfileForm((prev) => ({ ...prev, email: e.target.value }));
                      if (e.target.value.trim().toLowerCase() !== initialEmail.toLowerCase()) {
                        setShowEmailWarningBox(true);
                      } else {
                        setShowEmailWarningBox(false);
                      }
                    }}
                    className={`${masterModalInput} ${!isEditingEmail || isEmailVerified ? "bg-slate-100 text-slate-500" : ""}`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!isEditingEmail) {
                        setIsEditingEmail(true);
                        if (profileForm.email.trim().toLowerCase() !== initialEmail.toLowerCase()) {
                          setShowEmailWarningBox(true);
                        }
                      } else {
                        handleEmailAction();
                      }
                    }}
                    disabled={isEmailVerified}
                    className={`px-3 py-1 text-xs font-bold rounded-xl shrink-0 cursor-pointer transition-colors ${
                      isEmailVerified 
                        ? "bg-slate-400 text-white cursor-not-allowed" 
                        : isEditingEmail 
                          ? "bg-[#F4B429] hover:bg-[#e0a320] text-[#0E3652]" 
                          : "bg-[#0E3652] hover:bg-[#154668] text-white"
                    }`}
                  >
                    {isEmailVerified ? "Verified ✓" : isEditingEmail ? (otpSent ? "Confirm OTP" : "Verify") : "Edit"}
                  </button>
                </div>
              </div>

              {/* Warning message when changing email address */}
              {showEmailWarningBox && !isEmailVerified && (
                <div className="p-2.5 bg-amber-50 border border-amber-300 rounded-xl text-amber-900 text-xs font-bold tracking-[0.05em]">
                  ⚠️ Warning: This email address is your <strong>User ID</strong> for login. Are you sure you want to change it? OTP verification is required to update it.
                </div>
              )}

              {/* OTP Input row if Email verification was triggered */}
              {otpSent && !isEmailVerified && (
                <div>
                  <label className={masterModalLabel}>Enter 6-Digit OTP sent to new email</label>
                  <input
                    type="text"
                    maxLength={4}
                    value={emailOtp}
                    onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter OTP"
                    className={`${masterModalInput} tracking-widest text-center font-bold`}
                    required
                  />
                </div>
              )}

              {profileMessage.text && (
                <div className={profileMessage.type === "success" ? masterModalMsgSuccess : masterModalMsgError}>
                  {profileMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={profileLoading}
                className={masterModalSubmitBtn}
              >
                {profileLoading ? "Processing..." : "Save Profile Changes"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal (Untouched) */}
      {showPasswordModal && (
        <div className={masterModalOverlay}>
          <div className={masterModalBox}>
            <div className={masterModalHeader}>
              <div>
                <h2 className={masterModalTitle}>Change Password</h2>
                <p className={masterModalSubText}>You will be logged out automatically after updating.</p>
              </div>
              <button type="button" onClick={closePasswordModal} className={masterModalCloseBtn}>
                Cancel
              </button>
            </div>

            <form onSubmit={submitPasswordChange} className="space-y-1">
              <div>
                <label className={masterModalLabel}>Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                    className={masterModalInput}
                    required
                  />
                  <button type="button" onClick={() => setShowCurrentPassword((prev) => !prev)} className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-[#0E3652]">
                    {showCurrentPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={masterModalLabel}>New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                    className={masterModalInput}
                    required
                    minLength={4}
                  />
                  <button type="button" onClick={() => setShowNewPassword((prev) => !prev)} className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-[#0E3652]">
                    {showNewPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={masterModalLabel}>Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                    className={masterModalInput}
                    required
                    minLength={4}
                  />
                  <button type="button" onClick={() => setShowConfirmPassword((prev) => !prev)} className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-[#0E3652]">
                    {showConfirmPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {passwordMessage.text && (
                <div className={passwordMessage.type === "success" ? masterModalMsgSuccess : masterModalMsgError}>
                  {passwordMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={passwordLoading}
                className={masterModalSubmitBtn}
              >
                {passwordLoading ? "Updating..." : "Update Password"}
              </button>
            </form>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(2px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.15s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Header;